import { formatNumber } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, HostListener, inject, LOCALE_ID, OnInit, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, forkJoin, of } from 'rxjs';
import { AuthService } from 'src/app/services/auth-service';
import { CreditsService } from 'src/app/services/credits-service';
import { SnackbarService } from 'src/app/services/snackbar.service';
import { ComponentCanDeactivate } from 'src/app/services/unsaved-changes-guard';
import { UserApiService } from 'src/app/data-transfer/services/user-api-service';
import { BaseDataApiService } from 'src/app/data-transfer/services/base-data-api-service';
import { OrganizationUsersApiService } from 'src/app/data-transfer/services/organization-users-api-service';
import { UserGroupsApiService } from 'src/app/data-transfer/services/user-groups-api-service';
import { OrganizationFeaturesApiService } from 'src/app/data-transfer/services/organization-features-api-service';
import { PpwrApiService } from 'src/app/data-transfer/services/ppwr-api.service';
import { LicenseService } from 'src/app/services/licensing-service';
import { DocProfileDto } from 'src/app/data-transfer/entities/doc-entities/doc-profile-dto';
import { OrganizationFeaturesDto } from 'src/app/data-transfer/entities/organization-features-dto';
import { OrganizationUserGroupDto } from 'src/app/data-transfer/entities/user-group-entities/organization-user-group-dto';
import { OrganizationDto } from 'src/app/data-transfer/entities/organization-dto';
import { OrganizationUserDto } from 'src/app/data-transfer/entities/organization-user-dto';
import { CountryDto } from 'src/app/data-transfer/entities/country-dto';
import { getApiErrorMessage } from '../admin-api-error';
import { isLimitReached } from '../organization-limits';
import { CompanyDataComponent } from '../company-data/company-data.component';
import { PpwrRolesComponent } from '../ppwr-roles/ppwr-roles.component';

interface OverviewTileDetail {
  labelKey: string;
  value?: string;
  warn?: boolean;
}

interface OverviewTile {
  icon: string;
  labelKey: string;
  valueText?: string;
  suffixText?: string;
  suffixKey?: string;
  progress?: number;
  tone?: 'warning';
  details: OverviewTileDetail[];
}

interface GroupSummary {
  groupCount: number;
  membersAssigned: number;
}

const PLACEHOLDER_TILE_COUNT = 9;
const STORAGE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB'];

@Component({
  selector: 'app-organization-overview',
  templateUrl: './organization-overview.component.html',
  styleUrls: ['./organization-overview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrganizationOverviewComponent implements OnInit, ComponentCanDeactivate {
  private readonly userApiService = inject(UserApiService);
  private readonly baseDataApiService = inject(BaseDataApiService);
  private readonly organizationUsersApiService = inject(OrganizationUsersApiService);
  private readonly creditsService = inject(CreditsService);
  private readonly organizationFeaturesApiService = inject(OrganizationFeaturesApiService);
  private readonly locale = inject(LOCALE_ID);
  private readonly snackbarService = inject(SnackbarService);
  private readonly userGroupsApiService = inject(UserGroupsApiService);
  private readonly authService = inject(AuthService);
  private readonly ppwrApiService = inject(PpwrApiService);
  private readonly licenseService = inject(LicenseService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly organization = signal(new OrganizationDto());
  protected readonly allCountries = signal<CountryDto[]>([]);
  protected readonly docProfiles = signal<DocProfileDto[]>([]);
  private readonly users = signal<OrganizationUserDto[]>([]);
  private readonly groupSummary = signal<GroupSummary>({ groupCount: 0, membersAssigned: 0 });
  private readonly features = signal<OrganizationFeaturesDto | null>(null);
  private readonly isLoadingSignal = signal(true);
  protected readonly isLoading = this.isLoadingSignal.asReadonly();
  private readonly expandedTileIndexes = signal<ReadonlySet<number>>(new Set());

  private readonly placeholderTiles = Array.from({ length: PLACEHOLDER_TILE_COUNT }, (): OverviewTile => ({ icon: 'circle', labelKey: '', valueText: '', details: [] }));
  private readonly overviewTiles = computed(() => this.buildOverviewTiles(this.organization(), this.users(), this.groupSummary(), this.features()));
  protected readonly displayTiles = computed(() => (this.isLoadingSignal() ? this.placeholderTiles : this.overviewTiles()));

  private readonly companyData = viewChild(CompanyDataComponent);
  private readonly ppwrRoles = viewChild(PpwrRolesComponent);

  protected get canAdministrate(): boolean {
    return this.authService.canManageOrganization();
  }

  @HostListener('window:beforeunload')
  canDeactivate(): boolean {
    return !this.companyData()?.hasChanges() && !this.ppwrRoles()?.hasChanges();
  }

  ngOnInit(): void {
    forkJoin({
      organization: this.userApiService.getOwnOrganization(true),
      allCountries: this.baseDataApiService.getCountries(true),
      users: this.canAdministrate
        ? this.organizationUsersApiService.getUsers(true).pipe(
            catchError((error: HttpErrorResponse) => {
              this.snackbarService.showError(getApiErrorMessage(error));
              return of([] as OrganizationUserDto[]);
            })
          )
        : of([] as OrganizationUserDto[]),
      groups: this.canAdministrate ? this.userGroupsApiService.getGroups(true).pipe(
            catchError((error: HttpErrorResponse) => {
              this.snackbarService.showError(getApiErrorMessage(error));
              return of([] as OrganizationUserGroupDto[]);
            })
          ) : of([] as OrganizationUserGroupDto[]),
      features: this.organizationFeaturesApiService.getFeatures(true).pipe(
        catchError((error: HttpErrorResponse) => {
          this.snackbarService.showError(getApiErrorMessage(error));
          return of(null);
        })
      ),
      docProfiles:
        this.canAdministrate && this.licenseService.isPPWRAllowed
          ? this.ppwrApiService.getAllDocProfiles(true).pipe(
              catchError((error: HttpErrorResponse) => {
                this.snackbarService.showError(getApiErrorMessage(error));
                return of([] as DocProfileDto[]);
              })
            )
          : of([] as DocProfileDto[]),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ organization, allCountries, users, groups, features, docProfiles }) => {
          this.features.set(features);
          this.organization.set(organization);
          this.allCountries.set(allCountries);
          this.docProfiles.set(docProfiles);
          this.users.set(users);
          this.groupSummary.set({
            groupCount: groups.filter(group => !group.isRootGroup).length,
            membersAssigned: users.filter(user => !user.isDeleted && (user.userGroupIds?.length ?? 0) > 0).length,
          });
          this.isLoadingSignal.set(false);
        },
        error: error => {
          this.isLoadingSignal.set(false);
          this.snackbarService.showError(getApiErrorMessage(error));
        },
      });
  }

  // PPWR roles show the organization's address as its own manufacturer.
  protected onCompanyDataSaved(): void {
    this.ppwrRoles()?.reloadIfUnchanged();
  }

  protected isTileExpanded(index: number): boolean {
    return this.expandedTileIndexes().has(index);
  }

  protected setTileExpanded(index: number, expanded: boolean): void {
    this.expandedTileIndexes.update(indexes => {
      const next = new Set(indexes);
      if (expanded) {
        next.add(index);
      } else {
        next.delete(index);
      }
      return next;
    });
  }

  private buildOverviewTiles(organization: OrganizationDto, users: OrganizationUserDto[], groups: GroupSummary, features: OrganizationFeaturesDto | null): OverviewTile[] {
    const deactivatedUsers = users.filter(user => user.isDeleted);
    const activeUserCount = features?.activeUserCount ?? 0;
    const premiumUserCount = features?.premiumUserCount ?? 0;
    const maxUserCount = features?.maxUserCount ?? organization.maxUserCount ?? 0;
    const maxPremiumUserCount = features?.maxPremiumUserCount ?? organization.maxPremiumUserCount ?? 0;
    const premiumLimitReached = isLimitReached(premiumUserCount, features?.maxPremiumUserCount ?? organization.maxPremiumUserCount);
    const lcaCreditsInfinite = this.creditsService.areLcaCreditsInfinite();
    const recyclabilityCreditsInfinite = this.creditsService.areRycyclingCreditsInfinite();
    const totalCreditsDisplay =
      lcaCreditsInfinite || recyclabilityCreditsInfinite
        ? '∞'
        : String((this.creditsService.creditsCount.lcaCredits ?? 0) + (this.creditsService.creditsCount.recyclabilityCredits ?? 0));
    const enabledFeatureKeys = (
      [
        ['ppwr', features?.isPPWRAllowed],
        ['lca', features?.isLCAPossible],
        ['costCalculation', features?.isCostCalculationAllowed],
        ['excelDownload', features?.isExcelDownloadAllowed],
      ] as const
    )
      .filter(([, enabled]) => enabled)
      .map(([key]) => key);
    const datasetCounts = [
      { labelKey: 'home.packagingSystemHome.title', count: features?.packagingSystemCount ?? 0 },
      { labelKey: 'home.packagingUnitHome.title', count: features?.packagingUnitCount ?? 0 },
      { labelKey: 'home.componentHome.title', count: features?.packagingComponentCount ?? 0 },
      { labelKey: 'home.materialHome.title', count: features?.multiMaterialCount ?? 0 },
    ];
    const storageSizes = [
      { labelKey: 'fieldNamesForExport.images', size: features?.imageStorageSize ?? 0 },
      { labelKey: 'fieldNamesForExport.documents', size: features?.documentStorageSize ?? 0 },
      { labelKey: 'report.reports', size: features?.reportStorageSize ?? 0 },
    ];

    return [
      {
        icon: 'tag',
        labelKey: 'organizationAdmin.overview.tiles.organizationId.label',
        valueText: String(organization.id),
        details: [],
      },
      ...(features?.packageName
        ? [
            {
              icon: 'sell',
              labelKey: 'organizationAdmin.overview.tiles.package.label',
              valueText: features.packageName,
              details: [],
            },
          ]
        : []),
      {
        icon: 'group',
        labelKey: 'organizationAdmin.overview.tiles.activeUsers.label',
        valueText: String(activeUserCount),
        suffixText: maxUserCount > 0 ? `/ ${maxUserCount}` : undefined,
        progress: maxUserCount > 0 ? Math.min(100, Math.round((activeUserCount / maxUserCount) * 100)) : undefined,
        details: [
          { labelKey: 'organizationAdmin.overview.tiles.activeUsers.details.deactivated', value: String(deactivatedUsers.length) },
        ],
      },
      {
        icon: 'workspace_premium',
        labelKey: 'organizationAdmin.overview.tiles.premiumUsers.label',
        valueText: String(premiumUserCount),
        suffixText: maxPremiumUserCount > 0 ? `/ ${maxPremiumUserCount}` : undefined,
        progress: maxPremiumUserCount > 0 ? Math.min(100, Math.round((premiumUserCount / maxPremiumUserCount) * 100)) : undefined,
        tone: premiumLimitReached ? 'warning' : undefined,
        details: premiumLimitReached ? [{ labelKey: 'organizationAdmin.overview.tiles.premiumUsers.details.limitReached', value: '', warn: true }] : [],
      },
      {
        icon: 'groups',
        labelKey: 'organizationAdmin.overview.tiles.userGroups.label',
        valueText: String(groups.groupCount),
        suffixKey: 'organizationAdmin.overview.tiles.userGroups.suffix',
        details: [{ labelKey: 'organizationAdmin.overview.tiles.userGroups.details.membersAssigned', value: String(groups.membersAssigned) }],
      },
      {
        icon: 'toll',
        labelKey: 'organizationAdmin.overview.tiles.credits.label',
        valueText: totalCreditsDisplay,
        suffixKey: totalCreditsDisplay === '∞' ? undefined : 'organizationAdmin.overview.tiles.credits.suffix',
        details: [
          { labelKey: 'organizationAdmin.overview.tiles.credits.details.lca', value: lcaCreditsInfinite ? '∞' : String(this.creditsService.creditsCount.lcaCredits ?? 0) },
          {
            labelKey: 'organizationAdmin.overview.tiles.credits.details.recyclability',
            value: recyclabilityCreditsInfinite ? '∞' : String(this.creditsService.creditsCount.recyclabilityCredits ?? 0),
          },
        ],
      },
      {
        icon: 'flag',
        labelKey: 'organizationAdmin.overview.tiles.features.label',
        valueText: String(enabledFeatureKeys.length),
        suffixKey: 'organizationAdmin.overview.tiles.features.suffix',
        details: [
          { labelKey: 'organizationAdmin.overview.tiles.features.details.countries', value: String(features?.authorizedCountries.length ?? 0) },
          { labelKey: 'organizationAdmin.overview.tiles.features.details.environmentalEffects', value: String(features?.authorizedEnvironmentalEffects.length ?? 0) },
          ...enabledFeatureKeys.map(key => ({ labelKey: `organizationAdmin.overview.tiles.features.details.${key}`, value: '' })),
        ],
      },
      {
        icon: 'inventory_2',
        labelKey: 'organizationAdmin.overview.tiles.datasets.label',
        valueText: String(datasetCounts.reduce((sum, dataset) => sum + dataset.count, 0)),
        details: datasetCounts.map(dataset => ({ labelKey: dataset.labelKey, value: String(dataset.count) })),
      },
      {
        icon: 'cloud',
        labelKey: 'organizationAdmin.overview.tiles.storage.label',
        valueText: this.formatStorageSize(storageSizes.reduce((sum, storage) => sum + storage.size, 0)),
        details: [
          ...(features?.isStorageSizeIncomplete ? [{ labelKey: 'organizationAdmin.overview.tiles.storage.incomplete', value: '', warn: true }] : []),
          ...storageSizes.map(storage => ({ labelKey: storage.labelKey, value: this.formatStorageSize(storage.size) })),
        ],
      },
      {
        icon: 'tune',
        labelKey: 'fieldNamesForExport.customFields',
        valueText: String(features?.customFieldCount ?? 0),
        details: [],
      },
    ];
  }

  private formatStorageSize(bytes: number): string {
    let value = bytes;
    let unitIndex = 0;
    while (value >= 1024 && unitIndex < STORAGE_UNITS.length - 1) {
      value /= 1024;
      unitIndex++;
    }
    return `${formatNumber(value, this.locale, '1.0-1')} ${STORAGE_UNITS[unitIndex]}`;
  }
}